import { useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import Loader from "../../../Common/Loader/loader";
import {
  getAllComplaintZones,
  getComplaintCirclesByZoneId,
} from "../../../Apis/complaintApi";
import AddEditIeComplaintForm from "./AddEditIeComplaintForm";
import { ExportIeComplaint } from "./ExportIeComplaint";
import IeComplaintExpandableTable from "./IeComplaintExpandableTable";
import { IeComplaintCloseTimeMapContent } from "./IeComplaintCloseTimeMapView";
import {
  DEFAULT_REPORT_ZONE_ID,
  DEFAULT_REPORT_ZONE_NAME,
  useIeComplaintStore,
} from "./IeComplaintStore/useIeComplaintStore";
import { toast } from "react-toastify";
import {
  IE_COMPLAINT_MESSAGES,
  isValidFilterDateRange,
  isFutureFilterDate,
} from "./ieComplaintFormValidation";

const SELECT_MENU_PORTAL_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const IeComplaintRootComponent = () => {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [zoneWards, setZoneWards] = useState([]);
  const [viewCloseLocationRow, setViewCloseLocationRow] = useState(null);
  const [resolvedDefaultZoneId, setResolvedDefaultZoneId] = useState(
    DEFAULT_REPORT_ZONE_ID
  );
  const {
    filteredComplaints,
    filters,
    isLoading,
    kpiCategoryOptions,
    statusFilterOptions,
    updateFilter,
    applyFilters,
    resetFilters,
    fetchComplaints,
    fetchMasterOptions,
    fetchComplaintById,
    fetchActionHistory,
    changePage,
    changePageSize,
    pagination,
  } = useIeComplaintStore();

  const loadWards = async (zoneId) => {
    if (!zoneId) {
      setZoneWards([]);
      return;
    }

    try {
      const response = await getComplaintCirclesByZoneId(zoneId);
      const activeWards = response
        .filter((ward) => ward.isActive !== false)
        .map((ward) => ({
          ...ward,
          wardId: (ward.wardId ?? ward.circleId)?.toString(),
          zoneId: zoneId?.toString(),
          wardNo: ward.wardNo ?? ward.circleName,
        }));
      setZoneWards(activeWards);
      setWards((previous) => {
        const existingIds = new Set(previous.map((ward) => ward.wardId ?? ward.circleId));
        const merged = [...previous];
        activeWards.forEach((ward) => {
          if (!existingIds.has(ward.wardId)) merged.push(ward);
        });
        return merged;
      });
    } catch (error) {
      console.error("Error fetching wards:", error);
      setZoneWards([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      let zoneId = filters.zoneId || DEFAULT_REPORT_ZONE_ID;

      try {
        const response = await getAllComplaintZones();
        const activeZones = (Array.isArray(response) ? response : [])
          .filter(
            (zone) =>
              zone.isActive !== false
          )
          .map((zone) => ({
            ...zone,
            zoneId: zone.zoneId?.toString(),
          }));
        setZones(activeZones);

        const defaultZone =
          activeZones.find((zone) => zone.zoneName === DEFAULT_REPORT_ZONE_NAME) ||
          activeZones.find(
            (zone) => String(zone.zoneId) === DEFAULT_REPORT_ZONE_ID
          ) ||
          activeZones[0];
        zoneId = String(defaultZone?.zoneId ?? DEFAULT_REPORT_ZONE_ID);
        setResolvedDefaultZoneId(zoneId);
        updateFilter("zoneId", zoneId);
        await loadWards(zoneId);
      } catch (error) {
        console.error("Error fetching zones:", error);
        setZones([]);
        await loadWards(zoneId);
      }

      fetchMasterOptions();
      fetchComplaints({ zoneId, currentPage: 1 });
    };

    init();
  }, []);

  useEffect(() => {
    if (!filters.zoneId) return;
    loadWards(filters.zoneId);
  }, [filters.zoneId]);

  const handleFilterChange = (event) => {
    updateFilter(event.target.name, event.target.value);
  };

  const handleApplyFilters = async () => {
    const { fromDate, toDate } = filters;

    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      toast.error(IE_COMPLAINT_MESSAGES.invalidDateRange);
      return;
    }

    if (!isValidFilterDateRange(fromDate, toDate)) {
      toast.error(IE_COMPLAINT_MESSAGES.fromDateGreater);
      return;
    }

    if (isFutureFilterDate(fromDate, toDate)) {
      toast.error(IE_COMPLAINT_MESSAGES.futureDateNotAllowed);
      return;
    }

    await applyFilters();
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditRow(null);
    loadWards(resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID);
    setShowModal(true);
  };

  const handleEdit = async (row) => {
    const result = await fetchComplaintById(row.complaintId);
    const detail = result?.data || {};

    const pickValue = (...values) => {
      for (const value of values) {
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return "";
    };

    const mergedRow = {
      ...detail,
      ...row,
      issueTypeId: pickValue(row.issueTypeId, detail.issueTypeId),
      issueType: pickValue(row.issueType, detail.issueType),
      colonyId: pickValue(row.colonyId, detail.colonyId),
      colonyName: pickValue(row.colonyName, detail.colonyName),
      kpiTypeId: pickValue(row.kpiTypeId, detail.kpiTypeId),
      kpiCategory: pickValue(row.kpiCategory, detail.kpiCategory),
      beforeImage: pickValue(row.beforeImage, detail.beforeImage),
      afterImage: pickValue(row.afterImage, detail.afterImage),
      imageUrl: pickValue(row.imageUrl, detail.imageUrl),
      description: pickValue(detail.description, row.description),
      location: pickValue(detail.location, row.location),
      latitude: pickValue(detail.latitude, row.latitude),
      longitude: pickValue(detail.longitude, row.longitude),
      actionRemarks: pickValue(row.actionRemarks, detail.actionRemarks),
      closeTimeLatitude: pickValue(
        row.closeTimeLatitude,
        detail.closeTimeLatitude,
        detail.cLat
      ),
      closeTimeLongitude: pickValue(
        row.closeTimeLongitude,
        detail.closeTimeLongitude,
        detail.cLng
      ),
      closeTimeLocation: pickValue(
        row.closeTimeLocation,
        detail.closeTimeLocation,
        detail.cAddress
      ),
      complaintClosureOn: pickValue(
        row.complaintClosureOn,
        detail.complaintClosureOn,
        detail.modifiedDate
      ),
      closingImageUrl: pickValue(row.closingImageUrl, detail.closingImageUrl),
      isClosed: row.isClosed || detail.isClosed || row.status === "Closed",
    };

    setIsEditMode(true);
    setEditRow(mergedRow);
    if (mergedRow.zoneId) {
      await loadWards(mergedRow.zoneId);
    }
    setShowModal(true);
  };

  const handleResetFilters = async () => {
    const zoneId = resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID;
    await resetFilters(zoneId);
    await loadWards(zoneId);
  };

  const handlePageChange = (page) => {
    changePage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    changePageSize(pageSize);
  };

  const handleViewCloseLocation = (row) => {
    setViewCloseLocationRow(row);
  };

  const closeViewCloseLocationModal = () => {
    setViewCloseLocationRow(null);
  };

  const wardFilterOptions = useMemo(
    () =>
      zoneWards.map((ward) => ({
        value: String(ward.wardId ?? ward.circleId),
        label: ward.wardNo || ward.circleName,
      })),
    [zoneWards]
  );

  const kpiCategoryFilterOptions = useMemo(
    () =>
      kpiCategoryOptions.map((category) => ({
        value: String(category.kpiTypeId),
        label: `${category.kpiCode ? `${category.kpiCode} - ` : ""}${category.kpiType}`,
      })),
    [kpiCategoryOptions]
  );

  const allModalWards = useMemo(() => {
    const merged = [...wards, ...zoneWards];
    const seen = new Set();
    return merged.filter((ward) => {
      const id = ward.wardId ?? ward.circleId;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [wards, zoneWards]);

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">IE Complaint Information</h5>
             
            </div>
            <div className="d-flex align-items-center flex-wrap">
              <button type="button" className="button-container" onClick={handleAdd}>
                <FaPlus className="font-size-20 me-1" /> Add IE Complaint
              </button>
              <ExportIeComplaint complaintData={filteredComplaints} />
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Zone</label>
                  <select
                    name="zoneId"
                    value={
                      filters.zoneId ||
                      resolvedDefaultZoneId ||
                      DEFAULT_REPORT_ZONE_ID
                    }
                    onChange={handleFilterChange}
                    className="form-select bg-white"
                  >
                    {zones.map((zone) => (
                      <option key={zone.zoneId} value={zone.zoneId}>
                        {zone.zoneName}
                      </option>
                    ))}
                    {!zones.some(
                      (zone) =>
                        String(zone.zoneId) ===
                        String(resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID)
                    ) ? (
                      <option value={DEFAULT_REPORT_ZONE_ID}>
                        {DEFAULT_REPORT_ZONE_NAME}
                      </option>
                    ) : null}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Ward</label>
                  <Select
                    inputId="wardId"
                    name="wardId"
                    classNamePrefix="react-select"
                    options={wardFilterOptions}
                    value={
                      wardFilterOptions.find(
                        (option) => option.value === String(filters.wardId)
                      ) || null
                    }
                    onChange={(selected) =>
                      updateFilter("wardId", selected ? selected.value : "")
                    }
                    placeholder="Select Ward"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SELECT_MENU_PORTAL_STYLES}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">All KPI Type</label>
                  <Select
                    inputId="kpiCategory"
                    name="kpiCategory"
                    classNamePrefix="react-select"
                    options={kpiCategoryFilterOptions}
                    value={
                      kpiCategoryFilterOptions.find(
                        (option) => option.value === String(filters.kpiCategory)
                      ) || null
                    }
                    onChange={(selected) =>
                      updateFilter("kpiCategory", selected ? selected.value : "")
                    }
                    placeholder="Select KPI Type"
                    isClearable
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SELECT_MENU_PORTAL_STYLES}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                    max={new Date().toISOString().split("T")[0]}
                    className="form-control bg-white"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleFilterChange}
                    max={new Date().toISOString().split("T")[0]}
                    className="form-control bg-white"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="form-select bg-white"
                  >
                    <option value="">Select Status</option>
                    {statusFilterOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-12 mb-3 d-flex gap-2">
                  <button
                    type="button"
                    style={{
                      backgroundColor: "var(--btn-primary-bg)",
                      color: "var(--btn-primary-text)",
                      border: "none",
                      fontWeight: 600,
                      padding: "8px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    APPLY FILTERS
                  </button>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "#FDE8E8",
                      color: "#8B1A1A",
                      border: "none",
                      fontWeight: 600,
                      padding: "8px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={handleResetFilters}
                  >
                    RESET
                  </button>
                </div>
              </div>

              <div>
                <IeComplaintExpandableTable
                  rows={filteredComplaints}
                  onEdit={handleEdit}
                  onFetchActionHistory={fetchActionHistory}
                  onViewCloseLocation={handleViewCloseLocation}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddEditIeComplaintForm
        showModal={showModal}
        setShowModal={setShowModal}
        editRow={editRow}
        isEditMode={isEditMode}
        zones={zones}
        wards={allModalWards}
        onZoneChange={loadWards}
        defaultZoneId={resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID}
      />

      <Modal
        show={!!viewCloseLocationRow}
        onHide={closeViewCloseLocationModal}
        size="xl"
        centered
        aria-labelledby="ie-complaint-close-location-map-title"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ie-complaint-close-location-map-title">
            Close Time Location
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 16 }}>
          {viewCloseLocationRow && (
            <IeComplaintCloseTimeMapContent
              key={viewCloseLocationRow.complaintId}
              row={viewCloseLocationRow}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default IeComplaintRootComponent;
